import React, { useState } from 'react';
import { Calendar, MapPin, Users, DollarSign, Image as ImageIcon, ArrowLeft, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock events data
const mockEvents = [
  {
    id: 1,
    title: 'Comedy Night at The Laugh Factory',
    organizer: 'Mike\'s Comedy Shows',
    category: 'Comedy',
    date: '2026-03-15',
    time: '8:00 PM',
    location: 'The Laugh Factory, Hollywood',
    price: 25,
    image: 'https://images.unsplash.com/photo-1527224857830-43a7acc85260?auto=format&fit=crop&w=800&q=80',
    description: 'Join us for an evening of laughter with top comedians from LA!',
    capacity: 150
  },
  {
    id: 2,
    title: 'Live Music at Sunset Strip',
    organizer: 'LA Live Entertainment',
    category: 'Music',
    date: '2026-03-18',
    time: '9:00 PM',
    location: 'Whisky a Go Go, West Hollywood',
    price: 35,
    image: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?auto=format&fit=crop&w=800&q=80',
    description: 'Rock out with local bands and touring artists on the historic Sunset Strip!',
    capacity: 200
  },
  {
    id: 3,
    title: 'Hollywood Walking Tour & History',
    organizer: 'Hollywood Heritage Tours',
    category: 'Tour',
    date: '2026-03-20',
    time: '10:00 AM',
    location: 'Hollywood Blvd (Meet at Walk of Fame)',
    price: 15,
    image: 'https://images.unsplash.com/photo-1518929458119-e5bf444c30f4?auto=format&fit=crop&w=800&q=80',
    description: 'Discover the rich history of Hollywood with our expert guides!',
    capacity: 30
  },
  {
    id: 4,
    title: 'Stand-Up Comedy Open Mic Night',
    organizer: 'Comedy Underground',
    category: 'Comedy',
    date: '2026-03-22',
    time: '7:30 PM',
    location: 'The Comedy Store, West Hollywood',
    price: 10,
    image: 'https://images.unsplash.com/photo-1585699324551-f6c309eedeca?auto=format&fit=crop&w=800&q=80',
    description: 'See up-and-coming comedians test new material. Free drink included!',
    capacity: 100
  }
];

const EventsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    eventTitle: '',
    organizerName: '',
    category: '',
    date: '',
    time: '',
    location: '',
    price: '',
    capacity: '',
    description: '',
    imageUrl: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/event-submission`, {
        ...formData,
        price: parseFloat(formData.price),
        capacity: formData.capacity ? parseInt(formData.capacity) : null
      });
      
      toast({
        title: "Event Submitted!",
        description: "Thank you! We will review your event and contact you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        eventTitle: '',
        organizerName: '',
        category: '',
        date: '',
        time: '',
        location: '',
        price: '',
        capacity: '',
        description: '',
        imageUrl: '',
        contactEmail: '',
        contactPhone: ''
      });
      
    } catch (error) {
      console.error('Event submission error:', error);
      toast({
        title: "Event Received",
        description: "Your event has been saved for review. We'll contact you soon!",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950 text-white py-4 px-4 shadow-lg">
        <div className="container mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Main Site
          </Button>
          <h1 className="text-xl md:text-2xl font-black">Hollywood Events</h1>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <Badge className="mb-4 text-lg px-6 py-2 bg-yellow-500 text-black hover:bg-gray-100">
            For Event Organizers & Performers
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Hollywood Events &amp; Attractions
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            Reach thousands of tourists visiting the Hollywood Walk of Fame and Hollywood attractions. List your comedy shows, concerts, tours, and more!
          </p>
          <Button 
            size="lg"
            className="bg-yellow-500 text-black hover:bg-gray-100 font-bold px-8 py-6 text-lg"
            onClick={() => document.getElementById('submit-event').scrollIntoView({ behavior: 'smooth' })}
          >
            Submit Your Event
          </Button>
        </div>
      </section>

      {/* Featured Events */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 text-gray-900">
              Upcoming Hollywood <span className="text-yellow-600">Attractions &amp; Events</span>
            </h2>
            <p className="text-xl text-gray-600">
              Discover exciting entertainment, Hollywood sightseeing events, and experiences near the Walk of Fame
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockEvents.map((event) => (
              <Card key={event.id} className="hover:shadow-xl transition-shadow overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={event.image} 
                    alt={event.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <Badge className="mb-2 w-fit bg-yellow-600">{event.category}</Badge>
                  <CardTitle className="text-lg">{event.title}</CardTitle>
                  <p className="text-sm text-gray-600">by {event.organizer}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="h-4 w-4 text-yellow-600" />
                      <span>{new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock className="h-4 w-4 text-yellow-600" />
                      <span>{event.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="h-4 w-4 text-yellow-600" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <DollarSign className="h-4 w-4 text-yellow-600" />
                      <span className="font-bold">${event.price}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="h-4 w-4 text-yellow-600" />
                      <span>Up to {event.capacity} guests</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-4">{event.description}</p>
                  <Button className="w-full mt-4 bg-yellow-600 hover:bg-purple-700">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Event Form */}
      <section id="submit-event" className="py-20 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4 text-gray-900">
              Submit Your <span className="text-yellow-600">Event</span>
            </h2>
            <p className="text-xl text-gray-600">
              Fill out the form below to list your event. We review all submissions within 24 hours.
            </p>
          </div>

          <Card className="border-4 border-purple-500 shadow-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Event Title *
                  </label>
                  <Input
                    type="text"
                    name="eventTitle"
                    value={formData.eventTitle}
                    onChange={handleInputChange}
                    required
                    placeholder="Comedy Night at The Laugh Factory"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Organizer/Company Name *
                    </label>
                    <Input
                      type="text"
                      name="organizerName"
                      value={formData.organizerName}
                      onChange={handleInputChange}
                      required
                      placeholder="Your Name or Company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Event Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select category</option>
                      <option value="Comedy">Comedy Show</option>
                      <option value="Music">Live Music/Concert</option>
                      <option value="Tour">Walking Tour</option>
                      <option value="Nightlife">Nightlife/Club Event</option>
                      <option value="Food">Food & Dining Experience</option>
                      <option value="Art">Art Exhibition</option>
                      <option value="Other">Other Entertainment</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Event Date *
                    </label>
                    <Input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Time *
                    </label>
                    <Input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Ticket Price ($) *
                    </label>
                    <Input
                      type="number"
                      name="price"
                      min="0"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                      placeholder="25"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Venue/Location *
                    </label>
                    <Input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      placeholder="The Laugh Factory, Hollywood"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Capacity (Max Guests)
                    </label>
                    <Input
                      type="number"
                      name="capacity"
                      min="1"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      placeholder="100"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Event Description *
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    placeholder="Describe your event, what makes it special, what guests can expect..."
                    rows={5}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Event Image URL
                  </label>
                  <Input
                    type="url"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/event-image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <ImageIcon className="inline h-3 w-3 mr-1" />
                    Provide a high-quality image URL for your event listing
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Contact Email *
                    </label>
                    <Input
                      type="email"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleInputChange}
                      required
                      placeholder="events@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Contact Phone *
                    </label>
                    <Input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleInputChange}
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-yellow-600 p-4 rounded">
                  <p className="text-sm text-gray-700">
                    <Star className="inline h-4 w-4 mr-1 text-yellow-600" />
                    <strong>Note:</strong> All event submissions are reviewed by our team. We'll contact you within 24 hours to confirm your listing. There may be a small listing fee for featured placement.
                  </p>
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-yellow-600 hover:bg-purple-700 text-white font-bold py-6 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Event for Review'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-gray-400 mb-2">
            Part of{' '}
            <a 
              href="https://officialhollywoodtours.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-400 hover:underline"
            >
              Official Hollywood Tours LLC
            </a>
          </p>
          <p className="text-sm text-gray-500">
            &copy; 2026 Hollywood Fame Tour. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EventsPage;
